import { ensure } from "@holigral/shared/utils"
import type { Cell, Phase, PhaseStatistics, ResultMatrix } from "./model"
import { Colour, PHASES } from "./model"
import { useT } from "@/i18n"

const COLOURS: Record<Colour, string> = {
  [Colour.Red]: "#ef4444",
  [Colour.Orange]: "#f97316",
  [Colour.Yellow]: "#eab308",
  [Colour.Green]: "#22c55e",
  [Colour.Cyan]: "#06b6d4",
  [Colour.Blue]: "#3b82f6",
  [Colour.Violet]: "#8b5cf6",
}

// Black text on the light bands, white on the dark ones
const TEXT_COLOURS: Record<Colour, string> = {
  [Colour.Red]: "#ffffff",
  [Colour.Orange]: "#000000",
  [Colour.Yellow]: "#000000",
  [Colour.Green]: "#ffffff",
  [Colour.Cyan]: "#000000",
  [Colour.Blue]: "#ffffff",
  [Colour.Violet]: "#000000",
}

/**
 * Two decimals throughout, and not locale-formatted: the grid is read by
 * comparing figures down a column, which a swapped decimal separator
 * makes harder rather than easier.
 */
const decimals = (value: number) => value.toFixed(2)

const STAT_CELL = "px-2 text-right text-xs tabular-nums text-muted-foreground"
const STAT_HEAD = "px-2 text-right text-xs font-medium"

type CellProps = {
  readonly cell: Cell
}

function MatrixCell({ cell }: CellProps) {
  return (
    <td
      className="size-9 border border-background text-center text-xs tabular-nums"
      style={{
        backgroundColor: COLOURS[cell.colour],
        color: TEXT_COLOURS[cell.colour],
      }}
    >
      {cell.value}
    </td>
  )
}

type Props = {
  readonly matrix: ResultMatrix
}

/**
 * The 10×10 grid with its aggregates: row totals and dispersion down the
 * right edge, column totals and dispersion along the bottom.
 *
 * Cells are a function of the two totals alone, so two respondents with
 * the same totals produce the same grid however differently they
 * answered — the SD and kurtosis columns are what separates them (see
 * docs/profiling-maths.md §6).
 *
 * Both axes iterate PHASES rather than the serialised key order: five
 * parallel structures (sums, stats and the positional cells array) have
 * to stay aligned, and one canonical order is what guarantees it.
 */
export default function Matrix({ matrix }: Props) {
  const { t } = useT("matrix")

  const statOf = (stats: PhaseStatistics | undefined, phase: Phase) =>
    ensure(stats, `missing statistics for phase ${phase}`)

  const kurtosis = (stats: PhaseStatistics) =>
    stats.excessKurtosis === null
      ? t("stats.undefined")
      : decimals(stats.excessKurtosis)

  const grandTotal = PHASES.reduce(
    (sum, phase) => sum + matrix.rowSums[phase],
    0,
  )

  return (
    <div className="flex flex-col gap-2">
      <table className="border-collapse text-sm">
        <caption className="sr-only">{t("caption")}</caption>
        <thead>
          <tr>
            <td className="h-24" />
            {PHASES.map((phase) => (
              <th key={phase} scope="col" className="p-0 align-bottom">
                <div className="rotate-180 px-1 pb-1 text-xs font-medium whitespace-nowrap [writing-mode:vertical-rl]">
                  {t(`cols.${phase}`)}
                </div>
              </th>
            ))}
            <th scope="col" className={`${STAT_HEAD} align-bottom`}>
              <abbr title={t("stats.totalLabel")}>{t("stats.total")}</abbr>
            </th>
            <th scope="col" className={`${STAT_HEAD} align-bottom`}>
              <abbr title={t("stats.sdLabel")}>{t("stats.sd")}</abbr>
            </th>
            <th scope="col" className={`${STAT_HEAD} align-bottom`}>
              <abbr title={t("stats.kurtosisLabel")}>
                {t("stats.kurtosis")}
              </abbr>
            </th>
          </tr>
        </thead>
        <tbody>
          {PHASES.map((phase, rowIndex) => {
            const stats = statOf(matrix.rowStats[phase], phase)
            const cells = ensure(
              matrix.cells[rowIndex],
              `missing cells for row ${rowIndex}`,
            )

            return (
              <tr key={phase}>
                <th
                  scope="row"
                  className="px-2 py-1 text-right font-medium whitespace-nowrap"
                >
                  {t(`rows.${phase}`)}
                </th>
                {PHASES.map((colPhase, colIndex) => (
                  <MatrixCell
                    key={colPhase}
                    cell={ensure(
                      cells[colIndex],
                      `missing cell at ${rowIndex},${colIndex}`,
                    )}
                  />
                ))}
                <td className={`${STAT_CELL} text-foreground`}>
                  {matrix.rowSums[phase]}
                </td>
                <td className={STAT_CELL}>{decimals(stats.sd)}</td>
                <td className={STAT_CELL}>{kurtosis(stats)}</td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" className={STAT_HEAD}>
              <abbr title={t("stats.totalLabel")}>{t("stats.total")}</abbr>
            </th>
            {PHASES.map((phase) => (
              <td key={phase} className={`${STAT_CELL} text-foreground`}>
                {matrix.colSums[phase]}
              </td>
            ))}
            <td className={`${STAT_CELL} text-foreground`}>{grandTotal}</td>
            <td />
            <td />
          </tr>
          <tr>
            <th scope="row" className={STAT_HEAD}>
              <abbr title={t("stats.sdLabel")}>{t("stats.sd")}</abbr>
            </th>
            {PHASES.map((phase) => (
              <td key={phase} className={STAT_CELL}>
                {decimals(statOf(matrix.colStats[phase], phase).sd)}
              </td>
            ))}
            <td />
            <td />
            <td />
          </tr>
          <tr>
            <th scope="row" className={STAT_HEAD}>
              <abbr title={t("stats.kurtosisLabel")}>
                {t("stats.kurtosis")}
              </abbr>
            </th>
            {PHASES.map((phase) => (
              <td key={phase} className={STAT_CELL}>
                {kurtosis(statOf(matrix.colStats[phase], phase))}
              </td>
            ))}
            <td />
            <td />
            <td />
          </tr>
        </tfoot>
      </table>
      {/*
        The grid baseline sits outside the table on purpose: a row SD only
        means something against the respondent's own overall spread, but
        putting it in the SD row/SD column corner would read as "the SD of
        the SDs", which it is not.
      */}
      <p className="text-xs text-muted-foreground">
        <span className="font-medium">{t("stats.grid")}</span>{" "}
        {t("stats.gridSummary", {
          n: matrix.gridStats.n,
          total: grandTotal,
          sd: decimals(matrix.gridStats.sd),
          kurtosis: kurtosis(matrix.gridStats),
        })}
      </p>
    </div>
  )
}
